import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { activityApi } from '../api/endpoints.js';
import { ApiError } from '../api/client.js';
import { formatKcal, formatNumber } from '../lib/format.js';
import { FlameIcon, TrashIcon } from './icons.js';

interface ActivityCardProps {
  date: string;
  timeZone: string;
  /** The day's intake, shown beside the burn purely as context. */
  intakeKcal: number;
}

/**
 * Active energy for the day.
 *
 * Shown *next to* the intake, never subtracted from it. An adaptive calorie
 * target derived from intake and real weight change already accounts for every
 * calorie burned, so folding activity into the budget as well would count the
 * same training twice — and consumer devices overstate active energy badly
 * enough that the error would not be small.
 *
 * Entry is manual today. The API behind it already carries a source per record
 * and replaces a day per source, so a device sync can be added later without
 * touching this screen or the stored data.
 */
export function ActivityCard({ date, timeZone, intakeKcal }: ActivityCardProps): JSX.Element {
  const queryClient = useQueryClient();
  const [input, setInput] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  const dayQuery = useQuery({
    queryKey: ['activity', 'day', date],
    queryFn: () => activityApi.day({ date, timezone: timeZone }),
  });

  const invalidate = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ['activity'] }).then(() => undefined);

  const addActivity = useMutation({
    mutationFn: (payload: { activeKcal: number; label: string | null }) =>
      activityApi.add({ date, activeKcal: payload.activeKcal, label: payload.label }),
    onSuccess: async () => {
      setInput('');
      setLabel('');
      setError(null);
      await invalidate();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Speichern fehlgeschlagen.'),
  });

  const removeActivity = useMutation({
    mutationFn: (id: string) => activityApi.remove(id),
    onSuccess: invalidate,
  });

  const day = dayQuery.data;
  const totalKcal = day?.totalKcal ?? 0;

  function submit(): void {
    const parsed = Number(input.replace(',', '.'));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Bitte gib einen gültigen Wert in kcal ein.');
      return;
    }
    addActivity.mutate({ activeKcal: parsed, label: label.trim() || null });
  }

  return (
    <div className="card stack">
      <div className="row-between">
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FlameIcon />
          <span>Aktivität</span>
        </div>
        <span className="hint">{formatKcal(totalKcal)} kcal aktiv</span>
      </div>

      {totalKcal > 0 && (
        <div className="hint">
          Zufuhr {formatKcal(intakeKcal)} kcal · Verbrauch {formatKcal(totalKcal)} kcal.{' '}
          {/* Stated explicitly, because every other tracker silently adds it to
              the budget and the difference matters. */}
          Wird <strong>nicht</strong> auf dein Kalorienziel angerechnet.
        </div>
      )}

      <div className="row">
        <input
          className="input grow"
          inputMode="numeric"
          placeholder="kcal"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submit();
          }}
        />
        <input
          className="input grow"
          placeholder="z. B. Laufen (optional)"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submit();
          }}
        />
        <button className="btn btn-primary" disabled={addActivity.isPending} onClick={submit}>
          {addActivity.isPending ? <span className="spinner" /> : 'Eintragen'}
        </button>
      </div>

      {day && day.entries.length > 0 && (
        <div className="entry-list">
          {day.entries.map((entry) => (
            <div className="entry" key={entry.id}>
              <div className="grow">
                <div className="entry-name">{entry.label ?? 'Aktivität'}</div>
                <div className="entry-meta">
                  {formatKcal(entry.activeKcal)} kcal
                  {entry.steps != null ? ` · ${formatNumber(entry.steps)} Schritte` : ''}
                  {/* Manual entries are the norm for now; naming the source
                      keeps a later device sync distinguishable at a glance. */}
                  {entry.source !== 'manual' ? ` · ${entry.source}` : ''}
                </div>
              </div>
              <button
                className="icon-btn"
                aria-label="Eintrag löschen"
                disabled={removeActivity.isPending}
                onClick={() => removeActivity.mutate(entry.id)}
              >
                <TrashIcon />
              </button>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="banner banner-error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
